import * as Api from '@ViewModels';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { IVolunteerShiftTemplate, isVolunteerBoardDisabledError } from './models';
import { invalidateVolunteerShifts } from './useVolunteerShifts';

export const VOLUNTEER_SHIFT_TEMPLATES_KEY = '/volunteering/shiftTemplates';

export const useVolunteerShiftTemplatesQuery = ({
	activityId,
	enabled = true,
	impersonationContext,
}: {
	activityId?: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!activityId,
		queryKey: [VOLUNTEER_SHIFT_TEMPLATES_KEY, activityId, impersonationContext?.account?.id],
		queryFn: async () => {
			const value = await userSession.webServiceHelper.callAsync<IVolunteerShiftTemplate[]>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `volunteering/activities/${activityId}/shiftTemplates`,
				}),
				'GET'
			);
			return value ?? [];
		},
		meta: {
			toastErrorMessage: (error: Error) => (isVolunteerBoardDisabledError(error) ? '' : 'Failed to load weekly shifts'),
		},
	});
};

// Every template write can add, rewrite or remove generated shifts, even one that reports a partial failure.
const invalidateTemplatesAndShifts = (activityId: string) =>
	Promise.all([
		invalidateVolunteerShifts(activityId),
		reactQueryClient.invalidateQueries({ queryKey: [VOLUNTEER_SHIFT_TEMPLATES_KEY, activityId] }),
	]);

interface IMutationOptions {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: IVolunteerShiftTemplate) => void;
}

export const useCreateVolunteerShiftTemplateMutation = ({ onError, onSuccess }: IMutationOptions = {}) => {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({
			activityId,
			template,
			impersonationContext,
		}: {
			activityId: string;
			template: Partial<IVolunteerShiftTemplate>;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<IVolunteerShiftTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `volunteering/activities/${activityId}/shiftTemplates`,
				}),
				'POST',
				template
			),
		onSuccess,
		onError,
		onSettled: (_data, _error, variables) => invalidateTemplatesAndShifts(variables.activityId),
	});
};

export const useUpdateVolunteerShiftTemplateMutation = ({ onError, onSuccess }: IMutationOptions = {}) => {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({
			template,
			impersonationContext,
		}: {
			activityId: string;
			template: IVolunteerShiftTemplate;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<IVolunteerShiftTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `volunteering/shiftTemplates/${template.id}`,
				}),
				'PUT',
				template
			),
		onSuccess,
		onError,
		onSettled: (_data, _error, variables) => invalidateTemplatesAndShifts(variables.activityId),
	});
};

export const useArchiveVolunteerShiftTemplateMutation = ({ onError, onSuccess }: IMutationOptions = {}) => {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({
			templateId,
			impersonationContext,
		}: {
			activityId: string;
			templateId: string;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<IVolunteerShiftTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `volunteering/shiftTemplates/${templateId}`,
				}),
				'DELETE'
			),
		onSuccess,
		onError,
		onSettled: (_data, _error, variables) => invalidateTemplatesAndShifts(variables.activityId),
	});
};
